// G2 : fiche d'une inspection (téléphone et ordinateur, charte D-038). En tête, l'action attendue selon l'état
// et les droits calculés par le serveur (démarrer, terminer la saisie et transmettre, examiner, clôturer,
// déclarer une correction) ; puis quatre onglets : formulaire (rendu générique du modèle JSON, brouillon
// conservé chiffré sur l'appareil), constats, rapport et pièces, mission. Sans réseau, la fiche s'ouvre depuis
// le paquet de travail de l'appareil ; réponses et constats partent par la file hors connexion.

import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ErreurApi } from '../../api/client';
import { Message, MessageErreur, Squelette } from '../../composants/Etats';
import { date, dateHeure, heure, libelleCategorie, libelleProvince } from '../../composants/format';
import { Jauge } from '../../composants/Visualisations';
import { avancementFormulaire, avancementSection, FormulaireGenerique } from './FormulaireGenerique';
import { nouveauBrouillon, validerReponses, type Brouillon, type ErreurReponse } from './modele';
import {
  classeEtatConstat,
  classeEtatInspection,
  delaiDepasse,
  libelleDans,
  useReferentielInspections,
  type Constat,
  type DroitsInspection,
  type FicheInspectionDonnees,
} from './api';
import {
  conserverPhotoLocale,
  enregistrerBrouillonLocal,
  estErreurReseau,
  ETATS_EN_ATTENTE,
  horsLigneDisponible,
  LIBELLES_ETATS_OPERATION,
  lireBrouillonLocal,
  lirePaquet,
  mettreEnFile,
  operationsDeInspection,
  oublierBrouillonLocal,
  resoudrePhotosLocales,
} from './horsligne';
import { CLE_OPERATIONS, useEnLigne } from './Synchronisation';
import './terrain.css';

type Onglet = 'formulaire' | 'constats' | 'rapport' | 'mission';

const AUCUN_DROIT: DroitsInspection = { saisir: false, demarrer: false, constater: false, examiner: false, cloturer: false, annuler: false, leverConstats: false, declarerCorrection: false };

/** Fiche du serveur, ou, sans réseau, reconstituée depuis le paquet de travail conservé sur l'appareil. */
function useFiche(id: string | undefined) {
  const [repli, setRepli] = useState<{ fiche: FicheInspectionDonnees; enregistreLe: string } | null>(null);
  const requete = useQuery({
    queryKey: ['inspection', id],
    queryFn: async () => (await api<FicheInspectionDonnees>(`/api/v1/inspections/${id}`)).donnees,
    retry: (n, e) => !estErreurReseau(e) && !(e instanceof ErreurApi && e.statut >= 400) && n < 1,
  });
  useEffect(() => {
    if (!requete.error || !estErreurReseau(requete.error) || !horsLigneDisponible()) return;
    void lirePaquet().then((l) => {
      const m = l?.paquet.missions.find((x) => x.inspection.id === id);
      if (!l || !m) return;
      setRepli({
        enregistreLe: l.enregistreLe,
        fiche: {
          ...m.inspection,
          nature: 'programmee',
          debuteLe: null,
          transmiseLe: null,
          examineeLe: null,
          observationExamen: null,
          clotureeLe: null,
          motifCloture: null,
          motifAnnulation: null,
          rapportDocumentId: null,
          rapportEmpreinte: null,
          rapportValideLe: null,
          modifieLe: null,
          installation: { id: '', nom: m.installation?.nom ?? 'Installation', categorie: m.installation?.categorie ?? '', adresse: m.installation?.adresse ?? null, provinceCode: m.installation?.provinceCode ?? null, organisationNom: '' },
          formulaire: { ...m.formulaire, code: '' },
          contexte: m.contexte,
          equipe: [],
          constats: m.constats.map((c) => ({ ...c, suivis: [] })),
          pieces: [],
          // Hors connexion : seules la saisie et la consignation de constats sont possibles, si la mission est en cours.
          droits: { ...AUCUN_DROIT, saisir: m.inspection.etat === 'en_cours', constater: m.inspection.etat === 'en_cours' },
        },
      });
    });
  }, [requete.error, id]);
  return { requete, fiche: requete.data ?? repli?.fiche ?? null, repli };
}

function enDetails(e: unknown): ErreurReponse[] {
  if (e instanceof ErreurApi && Array.isArray(e.details)) {
    return (e.details as { chemin?: string; message?: string }[]).filter((d) => d.chemin && d.message).map((d) => ({ chemin: d.chemin!, message: d.message! }));
  }
  return [];
}

async function telechargerPiece(id: string) {
  const lien = await api<{ url: string }>(`/api/v1/inspections/pieces/${id}/lien`, { corps: {} });
  // Lien signé de 5 minutes, lié à l'utilisateur ; droits réévalués à l'accès.
  window.location.assign(lien.donnees.url);
}

interface EtatBrouillon {
  origine: 'serveur' | 'appareil';
  enregistreLe: string | null;
  /** Une saisie de l'appareil porte sur une version plus ancienne que celle du serveur. */
  perime: boolean;
}

export function FicheInspection() {
  const { id } = useParams();
  const client = useQueryClient();
  const referentiel = useReferentielInspections();
  const enLigne = useEnLigne();
  const { requete, fiche: f, repli } = useFiche(id);
  const [brouillon, setBrouillon] = useState<Brouillon | null>(null);
  const [etatBrouillon, setEtatBrouillon] = useState<EtatBrouillon>({ origine: 'serveur', enregistreLe: null, perime: false });
  const [onglet, setOnglet] = useState<Onglet>('formulaire');
  const [message, setMessage] = useState<string | null>(null);
  const [conflit, setConflit] = useState<{ versionServeur: number } | null>(null);
  const minuterie = useRef<number | null>(null);
  const locales = useQuery({ queryKey: [...CLE_OPERATIONS, 'inspection', id], queryFn: () => operationsDeInspection(id!), enabled: Boolean(id) && horsLigneDisponible() });
  const reponsesEnAttente = (locales.data ?? []).filter((o) => o.operation.type === 'reponses_inspection' && ETATS_EN_ATTENTE.includes(o.operation.etat));
  const constatsLocaux = (locales.data ?? []).filter((o) => o.operation.type === 'constat_inspection' && o.operation.etat !== 'synchronise');

  // Brouillon : la saisie de l'appareil prime si elle porte sur la version courante du serveur.
  useEffect(() => {
    if (!f) return;
    let annule = false;
    const base = nouveauBrouillon({ inspectionId: f.id, formulaireId: f.formulaire.id, formulaireVersion: f.formulaire.version, versionReponses: f.versionReponses, reponses: f.reponses });
    if (!horsLigneDisponible()) {
      setBrouillon(base);
      return;
    }
    void lireBrouillonLocal(f.id).then((local) => {
      if (annule) return;
      if (local && local.brouillon.formulaireId === f.formulaire.id) {
        setBrouillon(local.brouillon);
        setEtatBrouillon({ origine: 'appareil', enregistreLe: local.enregistreLe, perime: local.brouillon.versionReponses !== f.versionReponses });
      } else {
        setBrouillon(base);
        setEtatBrouillon({ origine: 'serveur', enregistreLe: null, perime: false });
      }
    });
    return () => {
      annule = true;
    };
  }, [f]);

  // Pour qui ne saisit pas (contrôle DGH, organisation inspectée), le rapport validé s'ouvre en premier.
  const premiereOuverture = useRef(true);
  useEffect(() => {
    if (!f || !premiereOuverture.current) return;
    premiereOuverture.current = false;
    if (!f.droits.saisir && f.rapportEmpreinte) setOnglet('rapport');
  }, [f]);

  const erreursLocales = useMemo(() => (f && brouillon ? validerReponses(f.formulaire.modele, brouillon.reponses, f.contexte, 'brouillon') : []), [f, brouillon]);
  const avancement = f && brouillon ? avancementFormulaire(f.formulaire.modele, f.contexte, brouillon) : { faites: 0, attendues: 0 };

  const rafraichir = async (texte?: string) => {
    setMessage(texte ?? null);
    await client.invalidateQueries({ queryKey: ['inspection', id] });
    await client.invalidateQueries({ queryKey: ['inspections'] });
    await client.invalidateQueries({ queryKey: CLE_OPERATIONS });
  };

  const action = useMutation({
    mutationFn: async (p: { chemin: string; corps?: unknown; succes: string }) => {
      await api(`/api/v1/inspections/${id}/${p.chemin}`, { methode: 'POST', corps: p.corps ?? {} });
      return p.succes;
    },
    onSuccess: (texte) => rafraichir(texte),
  });

  // Envoi des réponses : direct si le réseau répond, sinon par la file hors connexion (version de référence jointe).
  const envoiReponses = useMutation({
    mutationFn: async (mode: 'direct' | 'arbitrage') => {
      if (!f || !brouillon) return 'rien';
      if (mode === 'direct' && enLigne) {
        try {
          // Les photos prises sans réseau partent d'abord ; les réponses envoyées citent les documents créés.
          const { reponses } = horsLigneDisponible() ? await resoudrePhotosLocales(f.id, brouillon.reponses) : { reponses: brouillon.reponses };
          await api(`/api/v1/inspections/${f.id}/brouillon`, { methode: 'PUT', corps: { reponses, versionReponses: brouillon.versionReponses } });
          if (horsLigneDisponible()) await oublierBrouillonLocal(f.id);
          return 'serveur';
        } catch (e) {
          if (e instanceof ErreurApi && e.statut === 409) {
            const serveur = (e.details as { serveur?: { versionReponses?: number } } | null)?.serveur;
            setConflit({ versionServeur: serveur?.versionReponses ?? f.versionReponses });
            throw e;
          }
          if (!estErreurReseau(e) || !horsLigneDisponible()) throw e;
        }
      }
      if (!horsLigneDisponible()) throw new Error('Envoi impossible : ce navigateur ne permet pas la file hors connexion');
      await mettreEnFile('reponses_inspection', { inspectionId: f.id, reponses: brouillon.reponses }, String(brouillon.versionReponses));
      await oublierBrouillonLocal(f.id);
      return mode === 'arbitrage' ? 'arbitrage' : 'file';
    },
    onSuccess: async (r) => {
      setConflit(null);
      if (r === 'serveur') await rafraichir('Réponses enregistrées sur le serveur.');
      if (r === 'file') await rafraichir('Réponses enregistrées sur cet appareil, en attente d’envoi : elles partiront au retour du réseau.');
      if (r === 'arbitrage') await rafraichir('Votre saisie est envoyée avec sa version d’origine : le serveur la signalera en conflit pour arbitrage par un validateur de la DGH.');
    },
  });

  if (requete.isLoading && !f) return <Squelette lignes={8} libelle="Chargement de l’inspection" />;
  if (!f) return <MessageErreur erreur={requete.error} titre="Inspection introuvable ou hors de votre périmètre" />;
  if (!brouillon) return <Squelette lignes={8} libelle="Ouverture du formulaire" />;

  const saisieBloquee = reponsesEnAttente.length > 0;
  const peutSaisir = f.droits.saisir && !saisieBloquee;
  const modifie = etatBrouillon.origine === 'appareil' || JSON.stringify(brouillon.reponses) !== JSON.stringify(f.reponses);
  const pretATransmettre = f.droits.saisir && enLigne && !repli && !modifie && !saisieBloquee && avancement.faites === avancement.attendues && erreursLocales.length === 0;
  const constatsOuverts = f.constats.filter((c) => c.etat === 'ouvert' || c.etat === 'correction_declaree');

  const surChangement = (b: Brouillon) => {
    setBrouillon(b);
    if (!horsLigneDisponible()) return;
    if (minuterie.current) window.clearTimeout(minuterie.current);
    minuterie.current = window.setTimeout(() => {
      void enregistrerBrouillonLocal(b).then((le) => setEtatBrouillon((e) => ({ ...e, origine: 'appareil', enregistreLe: le })));
    }, 500);
  };
  const reprendreServeur = async () => {
    if (horsLigneDisponible()) await oublierBrouillonLocal(f.id);
    setConflit(null);
    setEtatBrouillon({ origine: 'serveur', enregistreLe: null, perime: false });
    setBrouillon(nouveauBrouillon({ inspectionId: f.id, formulaireId: f.formulaire.id, formulaireVersion: f.formulaire.version, versionReponses: f.versionReponses, reponses: f.reponses }));
    await rafraichir('Version du serveur reprise : la saisie de cet appareil a été abandonnée.');
  };
  // Photo : envoyée au serveur si le réseau répond, sinon conservée chiffrée sur l'appareil (cadrage, section 9)
  // et envoyée avec les réponses au retour du réseau.
  const televerserPhoto = async (fichier: File) => {
    if (enLigne && !repli) {
      const donnees = new FormData();
      donnees.append('fichier', fichier);
      try {
        const r = await api<{ documentId: string }>(`/api/v1/inspections/${f.id}/photos`, { formulaire: donnees });
        await client.invalidateQueries({ queryKey: ['inspection', id] });
        return r.donnees.documentId;
      } catch (e) {
        if (!estErreurReseau(e) || !horsLigneDisponible()) throw e;
      }
    }
    if (!horsLigneDisponible()) throw new Error('Photo impossible : ce navigateur ne permet pas la conservation hors connexion');
    return conserverPhotoLocale(f.id, fichier);
  };
  const photoPossible = peutSaisir && ((enLigne && !repli) || horsLigneDisponible());

  const onglets: { cle: Onglet; libelle: string }[] = [
    { cle: 'formulaire', libelle: 'Formulaire' },
    { cle: 'constats', libelle: `Constats (${f.constats.length + constatsLocaux.length})` },
    { cle: 'rapport', libelle: 'Rapport et pièces' },
    { cle: 'mission', libelle: 'Mission' },
  ];

  return (
    <div className="fiche-inspection">
      <div className="page-tete">
        <div>
          <span className="contexte">
            {f.reference}, {f.formulaire.titre} (version {f.formulaire.version})
          </span>
          <h1>{f.installation.nom}</h1>
          <div className="actions">
            <span className="attenue">{f.objet}</span>
            <span className={classeEtatInspection(f.etat)}>{libelleDans(referentiel.data?.etats, f.etat)}</span>
            <span className="attenue">prévu le {date(f.datePrevue)}</span>
          </div>
        </div>
        {f.installation.id && (
          <Link className="bouton" to={`/referentiel/installations/${f.installation.id}`}>
            Fiche de l’installation
          </Link>
        )}
      </div>

      {repli && (
        <p className="note ambre" role="status">
          Hors connexion : fiche enregistrée sur cet appareil le {dateHeure(repli.enregistreLe)}. Saisie, photos et constats sont conservés chiffrés et envoyés au retour du réseau ; la transmission exige le réseau.
        </p>
      )}
      {message && <Message type="succes">{message}</Message>}
      {action.error && <MessageErreur erreur={action.error} titre="Action refusée" />}
      {envoiReponses.error && !conflit && <MessageErreur erreur={envoiReponses.error} titre="Envoi refusé" />}

      <AFaire
        f={f}
        enLigne={enLigne}
        hors={Boolean(repli)}
        avancement={avancement}
        modifie={modifie}
        pret={pretATransmettre}
        saisieBloquee={saisieBloquee}
        constatsOuverts={constatsOuverts.length}
        conflit={conflit ?? (etatBrouillon.perime ? { versionServeur: f.versionReponses } : null)}
        enCours={action.isPending || envoiReponses.isPending}
        demarrer={() => action.mutate({ chemin: 'demarrage', succes: 'Contrôle démarré : la saisie est ouverte.' })}
        transmettre={() => action.mutate({ chemin: 'transmission', corps: { versionReponses: f.versionReponses }, succes: 'Rapport généré, validé (identité, date, empreinte SHA-256) et transmis à la DGH.' })}
        envoyer={() => envoiReponses.mutate('direct')}
        reprendreServeur={() => void reprendreServeur()}
        arbitrage={() => envoiReponses.mutate('arbitrage')}
        motiver={(chemin, corps, succes) => action.mutate({ chemin, corps, succes })}
        ouvrirConstats={() => setOnglet('constats')}
      />

      <div className="onglets" role="tablist" aria-label="Sections de la fiche">
        {onglets.map((o) => (
          <button key={o.cle} type="button" role="tab" id={`onglet-${o.cle}`} aria-selected={onglet === o.cle} aria-controls={`panneau-${o.cle}`} onClick={() => setOnglet(o.cle)}>
            {o.libelle}
          </button>
        ))}
      </div>

      {onglet === 'formulaire' && (
        <div id="panneau-formulaire" role="tabpanel" aria-labelledby="onglet-formulaire">
          {saisieBloquee && (
            <p className="note" role="status">
              Une saisie de ce formulaire est enregistrée sur cet appareil ({LIBELLES_ETATS_OPERATION[reponsesEnAttente[0]!.operation.etat].toLowerCase()}) : la modification reprendra après sa réception par le serveur.
            </p>
          )}
          <nav className="sommaire-sections" aria-label="Sections du formulaire">
            {f.formulaire.modele.sections.map((s, i) => {
              const a = avancementSection(s, f.contexte, brouillon);
              const incomplete = a.attendues > 0 && a.faites < a.attendues;
              return (
                <a key={s.code} href={`#section-${s.code}`} className={incomplete ? 'incomplete' : undefined}>
                  {i + 1}. {s.titre}
                  {incomplete && <span className="visuellement-cache"> (incomplète)</span>}
                </a>
              );
            })}
          </nav>
          <FormulaireGenerique
            modele={f.formulaire.modele}
            contexte={f.contexte}
            brouillon={brouillon}
            surChangement={surChangement}
            lectureSeule={!peutSaisir}
            erreurs={[...erreursLocales, ...enDetails(envoiReponses.error), ...enDetails(action.error)]}
            libellesProduits={referentiel.data?.produits}
            televerserPhoto={photoPossible ? televerserPhoto : undefined}
            motifSansPhoto={peutSaisir ? 'Photos : ce navigateur ne permet pas de les conserver hors connexion ; à joindre avec le réseau.' : undefined}
          />
          {peutSaisir && (
            <div className={modifie ? 'barre-saisie' : 'barre-saisie statique'}>
              <span role="status">
                {etatBrouillon.origine === 'appareil' && etatBrouillon.enregistreLe
                  ? `Enregistré sur cet appareil à ${heure(new Date(etatBrouillon.enregistreLe))}, non envoyé`
                  : modifie
                    ? 'Modifications en cours'
                    : `Enregistré sur le serveur (version ${f.versionReponses})`}
              </span>
              <span className="actions">
                <button type="button" className="bouton primaire" disabled={!modifie || erreursLocales.length > 0 || envoiReponses.isPending} onClick={() => envoiReponses.mutate('direct')}>
                  {enLigne && !repli ? 'Enregistrer sur le serveur' : 'Mettre en file d’envoi'}
                </button>
              </span>
            </div>
          )}
        </div>
      )}

      {onglet === 'constats' && (
        <div id="panneau-constats" role="tabpanel" aria-labelledby="onglet-constats">
          <ListeConstats constats={f.constats} referentiel={referentiel.data} droits={f.droits} surModification={rafraichir} enLigne={enLigne && !repli} />
          {constatsLocaux.length > 0 && (
            <section aria-labelledby="titre-constats-locaux">
              <h2 id="titre-constats-locaux" className="sur-titre">
                Constats saisis sur cet appareil
              </h2>
              <ul className="liste-cartes-constats">
                {constatsLocaux.map(({ operation, charge }) => (
                  <li key={operation.identifiantClient} className={operation.etat === 'refuse' || operation.etat === 'conflit' ? 'carte-constat rouge' : 'carte-constat ambre'}>
                    <div className="carte-bloc-tete">
                      <h3>{libelleDans(referentiel.data?.categoriesConstats, String(charge.categorie))}</h3>
                      <span className="etiquette ambre">{LIBELLES_ETATS_OPERATION[operation.etat]}</span>
                    </div>
                    <p>{String(charge.description)}</p>
                    <p className="attenue">Correction demandée : {String(charge.correctionDemandee)}</p>
                    {operation.motif && <p className="attenue">{operation.motif}</p>}
                  </li>
                ))}
              </ul>
            </section>
          )}
          {f.droits.constater && <NouveauConstat inspectionId={f.id} enLigne={enLigne && !repli} surCreation={rafraichir} categories={referentiel.data?.categoriesConstats ?? []} />}
        </div>
      )}

      {onglet === 'rapport' && (
        <div id="panneau-rapport" role="tabpanel" aria-labelledby="onglet-rapport">
          <RapportEtPieces f={f} />
        </div>
      )}

      {onglet === 'mission' && (
        <div id="panneau-mission" role="tabpanel" aria-labelledby="onglet-mission">
          <Mission f={f} natures={referentiel.data?.natures} />
        </div>
      )}
    </div>
  );
}

// En-tête d'action : ce que l'état et les droits demandent maintenant ---------------------------------

function AFaire(p: {
  f: FicheInspectionDonnees;
  enLigne: boolean;
  hors: boolean;
  avancement: { faites: number; attendues: number };
  modifie: boolean;
  pret: boolean;
  saisieBloquee: boolean;
  constatsOuverts: number;
  conflit: { versionServeur: number } | null;
  enCours: boolean;
  demarrer: () => void;
  transmettre: () => void;
  envoyer: () => void;
  reprendreServeur: () => void;
  arbitrage: () => void;
  motiver: (chemin: string, corps: unknown, succes: string) => void;
  ouvrirConstats: () => void;
}) {
  const { f } = p;
  if (p.conflit && f.droits.saisir) {
    return (
      <section className="a-faire rouge" aria-labelledby="titre-a-faire">
        <div>
          <span className="sur-titre">Conflit de saisie</span>
          <h2 id="titre-a-faire">Les réponses ont été modifiées sur le serveur (version {p.conflit.versionServeur})</h2>
          <p>Votre saisie porte sur une version plus ancienne. Reprenez la version du serveur, ou envoyez votre saisie pour arbitrage : un validateur de la DGH choisira la version retenue, avec trace.</p>
        </div>
        <div className="actions decision">
          <button type="button" className="bouton" disabled={p.enCours} onClick={p.reprendreServeur}>
            Reprendre la version du serveur
          </button>
          <button type="button" className="bouton primaire" disabled={p.enCours} onClick={p.arbitrage}>
            Envoyer ma saisie pour arbitrage
          </button>
        </div>
      </section>
    );
  }
  if (f.droits.demarrer) {
    return (
      <section className="a-faire" aria-labelledby="titre-a-faire">
        <div>
          <span className="sur-titre">À faire maintenant</span>
          <h2 id="titre-a-faire">Démarrer le contrôle sur place</h2>
          <p>Le démarrage ouvre la saisie du formulaire et la consignation des constats. Il exige le réseau ; ensuite, la saisie fonctionne aussi sans réseau.</p>
        </div>
        <button type="button" className="bouton primaire" disabled={p.enCours || !p.enLigne || p.hors} onClick={p.demarrer}>
          Démarrer le contrôle
        </button>
      </section>
    );
  }
  if (f.droits.saisir) {
    const restant = p.avancement.attendues - p.avancement.faites;
    return (
      <section className={restant > 0 || p.modifie ? 'a-faire' : 'a-faire neutre'} aria-labelledby="titre-a-faire">
        <div>
          <span className="sur-titre">À faire maintenant</span>
          <h2 id="titre-a-faire">
            {restant > 0 ? `${restant} réponse${restant > 1 ? 's' : ''} obligatoire${restant > 1 ? 's' : ''} à saisir` : p.modifie ? 'Enregistrer vos réponses sur le serveur' : 'Formulaire complet : transmettre le rapport'}
          </h2>
          <p>
            {p.saisieBloquee
              ? 'Une saisie attend son envoi depuis cet appareil.'
              : p.pret
                ? 'La transmission génère le rapport PDF, le valide par votre identité, la date et son empreinte SHA-256, puis le transmet à la DGH.'
                : 'La transmission exige le réseau, des réponses complètes et enregistrées sur le serveur.'}
          </p>
          <div className="avancement">
            <Jauge libelle="Réponses obligatoires" part={p.avancement.attendues === 0 ? 100 : (100 * p.avancement.faites) / p.avancement.attendues} valeur={`${p.avancement.faites} sur ${p.avancement.attendues}`} />
          </div>
        </div>
        <div className="actions decision">
          {p.modifie && !p.saisieBloquee && (
            <button type="button" className="bouton" disabled={p.enCours} onClick={p.envoyer}>
              {p.enLigne && !p.hors ? 'Enregistrer sur le serveur' : 'Mettre en file d’envoi'}
            </button>
          )}
          <button type="button" className="bouton primaire" disabled={!p.pret || p.enCours} onClick={p.transmettre}>
            Transmettre et valider le rapport
          </button>
        </div>
      </section>
    );
  }
  if (f.droits.examiner) {
    return (
      <section className="a-faire" aria-labelledby="titre-a-faire">
        <div>
          <span className="sur-titre">À faire maintenant, contrôle DGH</span>
          <h2 id="titre-a-faire">Examiner le rapport transmis le {dateHeure(f.transmiseLe)}</h2>
          <p>L’examen rend le rapport et les constats visibles de l’organisation inspectée. Un renvoi rouvre la saisie de l’inspecteur ; le rapport transmis reste conservé.</p>
        </div>
        <div className="actions decision">
          <ActionMotivee libelle="Valider l’examen" etiquette="Observation d’examen" champ="observation" chemin="examen" succes="Examen enregistré : l’organisation inspectée peut consulter le rapport et les constats." motiver={p.motiver} principale />
          <ActionMotivee libelle="Renvoyer pour compléments" etiquette="Motif du renvoi" champ="motif" chemin="renvoi" succes="Inspection renvoyée à l’inspecteur pour compléments." motiver={p.motiver} />
        </div>
      </section>
    );
  }
  if (f.droits.cloturer) {
    return (
      <section className="a-faire" aria-labelledby="titre-a-faire">
        <div>
          <span className="sur-titre">À faire maintenant, contrôle DGH</span>
          <h2 id="titre-a-faire">{p.constatsOuverts > 0 ? `Suivre ${p.constatsOuverts} constat${p.constatsOuverts > 1 ? 's' : ''} non levé${p.constatsOuverts > 1 ? 's' : ''} avant la clôture` : 'Clôturer l’inspection'}</h2>
          <p>{p.constatsOuverts > 0 ? 'Levez les constats corrigés, ou clôturez en le motivant explicitement : le motif est conservé.' : 'Tous les constats sont levés ou annulés.'}</p>
        </div>
        <div className="actions decision">
          {p.constatsOuverts > 0 && (
            <button type="button" className="bouton" onClick={p.ouvrirConstats}>
              Voir les constats
            </button>
          )}
          <ActionMotivee libelle="Clôturer" etiquette="Motif de clôture" champ="motif" chemin="cloture" succes="Inspection clôturée." motiver={p.motiver} forcer={p.constatsOuverts > 0} principale />
        </div>
      </section>
    );
  }
  if (f.droits.declarerCorrection && p.constatsOuverts > 0) {
    return (
      <section className="a-faire" aria-labelledby="titre-a-faire">
        <div>
          <span className="sur-titre">À faire maintenant</span>
          <h2 id="titre-a-faire">{`${p.constatsOuverts} constat${p.constatsOuverts > 1 ? 's demandent' : ' demande'} une correction`}</h2>
          <p>Déclarez chaque correction avec un justificatif ; la DGH lève le constat ou le maintient ouvert.</p>
        </div>
        <button type="button" className="bouton primaire" onClick={p.ouvrirConstats}>
          Voir les constats
        </button>
      </section>
    );
  }
  if (f.etat === 'transmise') {
    return (
      <p className="note" role="status">
        Rapport transmis le {dateHeure(f.transmiseLe)} : examen par la DGH en attente.
      </p>
    );
  }
  return null;
}

function ActionMotivee(p: {
  libelle: string;
  etiquette: string;
  champ: 'motif' | 'observation';
  chemin: string;
  succes: string;
  motiver: (chemin: string, corps: unknown, succes: string) => void;
  principale?: boolean;
  forcer?: boolean;
  danger?: boolean;
}) {
  const [ouvert, setOuvert] = useState(false);
  const [texte, setTexte] = useState('');
  const [force, setForce] = useState(false);
  const classe = p.danger ? 'bouton danger' : p.principale ? 'bouton primaire' : 'bouton';
  if (!ouvert) {
    return (
      <button type="button" className={classe} onClick={() => setOuvert(true)}>
        {p.libelle}
      </button>
    );
  }
  const id = `motif-${p.chemin}`;
  return (
    <form
      className="action-motivee"
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        p.motiver(p.chemin, { [p.champ]: texte, ...(p.forcer !== undefined ? { forcer: force } : {}) }, p.succes);
        setOuvert(false);
      }}
    >
      <div className="champ">
        <label htmlFor={id}>{p.etiquette}</label>
        <textarea id={id} rows={2} value={texte} onChange={(e) => setTexte(e.target.value)} />
      </div>
      {p.forcer && (
        <label className="case">
          <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} />
          <span>Clôturer malgré les constats non levés (le motif est conservé)</span>
        </label>
      )}
      <div className="actions">
        <button type="button" className="bouton discret" onClick={() => setOuvert(false)}>
          Annuler
        </button>
        <button type="submit" className={classe} disabled={texte.trim().length < 3 || (p.forcer === true && !force)}>
          {p.libelle}
        </button>
      </div>
    </form>
  );
}

// Constats -------------------------------------------------------------------------------------------

function ListeConstats(p: {
  constats: Constat[];
  referentiel: { categoriesConstats: { code: string; libelle: string }[]; etatsConstats: { code: string; libelle: string }[] } | undefined;
  droits: DroitsInspection;
  surModification: (texte?: string) => Promise<void>;
  enLigne: boolean;
}) {
  const [erreur, setErreur] = useState<unknown>(null);
  const suivre = async (constatId: string, action: string, commentaire: string, fichier?: File | null) => {
    setErreur(null);
    try {
      let documentId: string | undefined;
      if (fichier) {
        const d = new FormData();
        d.append('fichier', fichier);
        documentId = (await api<{ documentId: string }>(`/api/v1/constats/${constatId}/justificatifs`, { formulaire: d })).donnees.documentId;
      }
      await api(`/api/v1/constats/${constatId}/suivi`, { corps: { action, commentaire, documentId } });
      await p.surModification('Suivi du constat enregistré.');
    } catch (e) {
      setErreur(e);
    }
  };
  if (p.constats.length === 0) return <p className="attenue">Aucun constat reçu par le serveur pour cette inspection.</p>;
  const ordre = [...p.constats].sort((a, b) => Number(delaiDepasse(b)) - Number(delaiDepasse(a)) || a.numero - b.numero);
  return (
    <>
      {erreur !== null && <MessageErreur erreur={erreur} titre="Suivi refusé" />}
      <ul className="liste-cartes-constats">
        {ordre.map((c) => {
          const depasse = delaiDepasse(c);
          return (
            <li key={c.id} className={depasse ? 'carte-constat rouge' : c.etat === 'correction_declaree' ? 'carte-constat ambre' : 'carte-constat'}>
              <div className="carte-bloc-tete">
                <h3>
                  Constat {c.numero} : {libelleDans(p.referentiel?.categoriesConstats, c.categorie)}
                </h3>
                <span className={classeEtatConstat(c.etat, depasse)}>{depasse ? 'Délai dépassé' : libelleDans(p.referentiel?.etatsConstats, c.etat)}</span>
              </div>
              <p>{c.description}</p>
              <p>
                <b>Correction demandée :</b> {c.correctionDemandee}
                {c.delai ? <span className="attenue"> (avant le {date(c.delai)})</span> : null}
              </p>
              {c.suivis.length > 0 && (
                <ol className="historique" aria-label={`Suivi du constat ${c.numero}`}>
                  {c.suivis.map((s) => (
                    <li key={s.id}>
                      <time dateTime={s.creeLe}>{dateHeure(s.creeLe)}</time>
                      <span>
                        {s.auteur ?? 'Auteur non lisible'} : {s.commentaire}
                      </span>
                    </li>
                  ))}
                </ol>
              )}
              {p.enLigne && <SuiviConstat constat={c} droits={p.droits} suivre={suivre} />}
            </li>
          );
        })}
      </ul>
    </>
  );
}

function SuiviConstat({ constat, droits, suivre }: { constat: Constat; droits: DroitsInspection; suivre: (id: string, action: string, commentaire: string, fichier?: File | null) => Promise<void> }) {
  const [commentaire, setCommentaire] = useState('');
  const [fichier, setFichier] = useState<File | null>(null);
  const actions: { code: string; libelle: string; principale?: boolean }[] = [];
  const ouvert = constat.etat === 'ouvert' || constat.etat === 'correction_declaree';
  if (droits.declarerCorrection && constat.etat === 'ouvert') actions.push({ code: 'declarer_correction', libelle: 'Déclarer la correction', principale: true });
  if (droits.leverConstats && ouvert) actions.push({ code: 'lever', libelle: 'Lever le constat', principale: constat.etat === 'correction_declaree' });
  if (droits.leverConstats && constat.etat === 'correction_declaree') actions.push({ code: 'maintenir', libelle: 'Maintenir ouvert' });
  if (droits.leverConstats && ouvert) actions.push({ code: 'annuler', libelle: 'Annuler le constat' });
  if (actions.length === 0) return null;
  return (
    <div className="encart">
      <div className="formulaire-ligne">
        <div className="champ">
          <label htmlFor={`sc-${constat.id}`}>Commentaire</label>
          <input id={`sc-${constat.id}`} value={commentaire} onChange={(e) => setCommentaire(e.target.value)} />
        </div>
        {droits.declarerCorrection && constat.etat === 'ouvert' && (
          <div className="champ">
            <label htmlFor={`sj-${constat.id}`}>Justificatif (PDF, JPEG ou PNG)</label>
            <input id={`sj-${constat.id}`} type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setFichier(e.target.files?.[0] ?? null)} />
          </div>
        )}
      </div>
      <div className="actions">
        {actions.map((a) => (
          <button key={a.code} type="button" className={a.principale ? 'bouton primaire' : 'bouton'} disabled={commentaire.trim().length < 3} onClick={() => void suivre(constat.id, a.code, commentaire, a.code === 'declarer_correction' ? fichier : null)}>
            {a.libelle}
          </button>
        ))}
      </div>
    </div>
  );
}

function NouveauConstat({ inspectionId, enLigne, surCreation, categories }: { inspectionId: string; enLigne: boolean; surCreation: (t?: string) => Promise<void>; categories: { code: string; libelle: string }[] }) {
  const [categorie, setCategorie] = useState('');
  const [description, setDescription] = useState('');
  const [correction, setCorrection] = useState('');
  const [delai, setDelai] = useState('');
  // Identifiant client conservé jusqu'au succès : un renvoi après une coupure ne crée pas de doublon.
  const [identifiantClient, setIdentifiantClient] = useState(() => crypto.randomUUID());
  const envoi = useMutation({
    mutationFn: async () => {
      const charge = { inspectionId, categorie, description, correctionDemandee: correction, ...(delai ? { delai } : {}) };
      if (enLigne) {
        try {
          await api(`/api/v1/inspections/${inspectionId}/constats`, { corps: { identifiantClient, ...charge } });
          return 'serveur';
        } catch (e) {
          if (!estErreurReseau(e) || !horsLigneDisponible()) throw e;
        }
      }
      if (!horsLigneDisponible()) throw new Error('Consignation impossible sans réseau sur ce navigateur');
      await mettreEnFile('constat_inspection', charge);
      return 'file';
    },
    onSuccess: async (r) => {
      setCategorie('');
      setDescription('');
      setCorrection('');
      setDelai('');
      setIdentifiantClient(crypto.randomUUID());
      await surCreation(r === 'serveur' ? 'Constat consigné.' : 'Constat enregistré sur cet appareil, en attente d’envoi au serveur.');
    },
  });
  return (
    <form
      className="carte-bloc"
      aria-labelledby="titre-nouveau-constat"
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        envoi.mutate();
      }}
    >
      <h2 id="titre-nouveau-constat">Consigner un constat</h2>
      <p className="attenue">Décrivez les faits observés et la correction demandée. Aucune qualification n’est portée sur l’organisation inspectée.</p>
      <div className="formulaire-ligne">
        <div className="champ">
          <label htmlFor="nc-categorie">Catégorie</label>
          <select id="nc-categorie" value={categorie} onChange={(e) => setCategorie(e.target.value)}>
            <option value="">Choisir</option>
            {categories.map((c) => (
              <option key={c.code} value={c.code}>
                {c.libelle}
              </option>
            ))}
          </select>
        </div>
        <div className="champ">
          <label htmlFor="nc-delai">Délai de correction (facultatif)</label>
          <input id="nc-delai" type="date" value={delai} onChange={(e) => setDelai(e.target.value)} />
        </div>
      </div>
      <div className="champ">
        <label htmlFor="nc-description">Faits observés</label>
        <textarea id="nc-description" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
      </div>
      <div className="champ">
        <label htmlFor="nc-correction">Correction demandée</label>
        <textarea id="nc-correction" rows={2} value={correction} onChange={(e) => setCorrection(e.target.value)} />
      </div>
      {envoi.error && <MessageErreur erreur={envoi.error} titre="Constat refusé" />}
      <button type="submit" className="bouton primaire" disabled={!categorie || description.trim().length < 3 || correction.trim().length < 3 || envoi.isPending}>
        {enLigne ? 'Consigner le constat' : 'Enregistrer sur cet appareil'}
      </button>
    </form>
  );
}

// Rapport, pièces, mission -----------------------------------------------------------------------------

function RapportEtPieces({ f }: { f: FicheInspectionDonnees }) {
  const [erreur, setErreur] = useState<unknown>(null);
  const rapports = f.pieces.filter((x) => x.categorie === 'rapport_inspection');
  const autres = f.pieces.filter((x) => x.categorie !== 'rapport_inspection');
  return (
    <div className="deux-colonnes">
      <section className="principal carte-bloc" aria-labelledby="titre-rapport">
        <h2 id="titre-rapport">Rapport validé</h2>
        {f.rapportEmpreinte ? (
          <dl className="paires">
            <div>
              <dt>Validé et transmis le</dt>
              <dd>{dateHeure(f.rapportValideLe ?? f.transmiseLe)}</dd>
            </div>
            <div>
              <dt>Validation simple</dt>
              <dd>Identité de l’inspecteur, date, empreinte du document</dd>
            </div>
            <div>
              <dt>Empreinte SHA-256</dt>
              <dd className="empreinte">{f.rapportEmpreinte}</dd>
            </div>
            {f.examineeLe && (
              <div>
                <dt>Examiné le</dt>
                <dd>
                  {dateHeure(f.examineeLe)}
                  <small>{f.observationExamen}</small>
                </dd>
              </div>
            )}
            {f.clotureeLe && (
              <div>
                <dt>Clôturé le</dt>
                <dd>
                  {dateHeure(f.clotureeLe)}
                  <small>{f.motifCloture}</small>
                </dd>
              </div>
            )}
          </dl>
        ) : (
          <p className="attenue">Le rapport PDF est généré à la transmission par l’inspecteur ; la signature électronique qualifiée reste à confirmer par les autorités.</p>
        )}
        {rapports.length > 0 && (
          <ul className="liste-doublons">
            {rapports.map((r) => (
              <li key={r.id}>
                <span>
                  <b>{r.titre}</b>
                  <small>
                    {Math.ceil(r.taille / 1024)} Ko, {dateHeure(r.creeLe)}
                  </small>
                </span>
                <button type="button" className="bouton" onClick={() => void telechargerPiece(r.id).catch(setErreur)}>
                  Télécharger
                </button>
              </li>
            ))}
          </ul>
        )}
        {erreur !== null && <MessageErreur erreur={erreur} titre="Téléchargement refusé" />}
      </section>
      <aside className="lateral">
        <section className="carte-bloc" aria-labelledby="titre-pieces">
          <h2 id="titre-pieces">Photos et justificatifs</h2>
          {autres.length === 0 ? (
            <p className="attenue">Aucune photo ni justificatif.</p>
          ) : (
            <ul className="liste-doublons">
              {autres.map((x) => (
                <li key={x.id}>
                  <span>
                    <b>{x.titre}</b>
                    <small>
                      {x.categorie === 'photo_inspection' ? 'Photo' : 'Justificatif'}, {Math.ceil(x.taille / 1024)} Ko
                    </small>
                  </span>
                  <button type="button" className="bouton" aria-label={`Télécharger ${x.titre}`} onClick={() => void telechargerPiece(x.id).catch(setErreur)}>
                    Télécharger
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      </aside>
    </div>
  );
}

function Mission({ f, natures }: { f: FicheInspectionDonnees; natures: { code: string; libelle: string }[] | undefined }) {
  const evenements = [
    { date: f.debuteLe, libelle: 'Contrôle démarré' },
    { date: f.modifieLe, libelle: 'Dernière modification' },
    { date: f.transmiseLe, libelle: 'Rapport transmis' },
    { date: f.examineeLe, libelle: 'Rapport examiné par la DGH' },
    { date: f.clotureeLe, libelle: 'Inspection clôturée' },
  ].filter((e): e is { date: string; libelle: string } => Boolean(e.date));
  return (
    <div className="deux-colonnes">
      <section className="principal carte-bloc" aria-labelledby="titre-mission">
        <h2 id="titre-mission">Mission</h2>
        <dl className="paires">
          <div>
            <dt>Installation</dt>
            <dd>
              {f.installation.nom}
              <small>{[libelleCategorie(f.installation.categorie), f.installation.organisationNom].filter(Boolean).join(', ')}</small>
            </dd>
          </div>
          <div>
            <dt>Province</dt>
            <dd>{libelleProvince(f.installation.provinceCode)}</dd>
          </div>
          {f.installation.adresse && (
            <div>
              <dt>Adresse</dt>
              <dd>{f.installation.adresse}</dd>
            </div>
          )}
          <div>
            <dt>Nature</dt>
            <dd>{libelleDans(natures, f.nature)}</dd>
          </div>
          <div>
            <dt>Objet</dt>
            <dd>{f.objet}</dd>
          </div>
          <div>
            <dt>Date prévue</dt>
            <dd>{date(f.datePrevue)}</dd>
          </div>
          <div>
            <dt>Inspecteurs</dt>
            <dd>{f.equipe.length ? f.equipe.map((u) => `${u.prenom} ${u.nom}`).join(', ') : 'non lisible hors connexion'}</dd>
          </div>
          <div>
            <dt>Formulaire</dt>
            <dd>
              {f.formulaire.titre}
              <small>version {f.formulaire.version}</small>
            </dd>
          </div>
          {f.motifAnnulation && (
            <div>
              <dt>Motif d’annulation</dt>
              <dd>{f.motifAnnulation}</dd>
            </div>
          )}
        </dl>
      </section>
      <aside className="lateral">
        <section className="carte-bloc" aria-labelledby="titre-historique">
          <h2 id="titre-historique">Historique</h2>
          {evenements.length === 0 ? (
            <p className="attenue">Contrôle non démarré.</p>
          ) : (
            <ol className="historique">
              {evenements.map((e) => (
                <li key={e.libelle}>
                  <time dateTime={e.date}>{dateHeure(e.date)}</time>
                  <span>{e.libelle}</span>
                </li>
              ))}
            </ol>
          )}
        </section>
      </aside>
    </div>
  );
}
